import {useEffect} from 'react';
import {view} from '@sail/ui';
import {MEDIA_QUERIES} from 'src/v2/util/mediaQueries';
import {useSelector, setValue} from 'src/v2/state/store';
import {WalletSelectScreen} from 'src/v2/components/walletSelect/WalletSelectScreen';
import {WalletConnectScreen} from 'src/v2/components/walletConnect/WalletConnectScreen';
import {NetworkSwitchScreen} from 'src/v2/components/networkSwitch/NetworkSwitchScreen';
import {WalletSwitchScreen} from 'src/v2/components/walletSwitch/WalletSwitchScreen';
import {PayScreen} from 'src/v2/components/pay/PayScreen';
import {SetupScreen} from 'src/v2/components/setup/SetupScreen';
import {SuccessScreen} from 'src/v2/components/success/SuccessScreen';
import {ProcessingScreen} from 'src/v2/components/processing/ProcessingScreen';
import {CanceledScreen} from 'src/v2/components/canceled/CanceledScreen';
import {ErroredScreen} from 'src/v2/components/error/ErroredScreen';
import {InstallWalletScreen} from 'src/v2/components/installWallet/InstallWalletScreen';
import {ExchangeScreen} from 'src/v2/components/exchangeScreen/ExchangeScreen';
import {AnyCryptoScreen} from 'src/v2/components/anyCrypto/AnyCryptoScreen';
import {AnyCryptoProgressScreen} from 'src/v2/components/anyCrypto/progress/AnyCryptoProgressScreen';
import {TokenSelectScreen} from 'src/v2/components/anyCrypto/tokenSelect/TokenSelectScreen';
import {NetworkSelectScreen} from 'src/v2/components/anyCrypto/networkSelect/NetworkSelectScreen';
import {RefundScreen} from 'src/v2/components/anyCrypto/refund/RefundScreen';
import {NetworkSwitchingOverlay} from 'src/v2/components/overlays/NetworkSwitchingOverlay';
import {logger} from 'src/utils/logger';
import {validateWalletForCheckout} from 'src/v2/util/validateWalletForCheckout';
import {useMiradorIds} from 'src/v2/state/fingerprinting/useMiradorIds';

const ScreenComponent = () => {
  const screen = useSelector((state) => state.screen);
  const activeWallet = useSelector(
    (state) => state.walletConnection.activeWallet,
  );
  const connectedWallets = useSelector(
    (state) => state.walletConnection.connectedWallets,
  );
  const walletConnectSupportedCurrencies = useSelector(
    (state) => state.walletConnectSupportedCurrencies,
  );

  // Clear the error banner when the screen changes
  useEffect(() => {
    setValue((state) => {
      state.errorBanner = null;
    });

    logger.log('crypto_payin_session.screen.rendered', {
      screen,
    });
  }, [screen]);

  switch (screen) {
    case 'WALLET_SELECT':
      return <WalletSelectScreen />;
    case 'WALLET_SWITCH':
      return <WalletSwitchScreen />;
    case 'NETWORK_SELECT':
      return <WalletConnectScreen />;
    case 'NETWORK_SWITCH':
      return <NetworkSwitchScreen />;
    case 'EXCHANGE':
      return <ExchangeScreen />;
    case 'PAY':
    case 'SETUP': {
      const result = validateWalletForCheckout({
        activeWallet,
        connectedWallets,
        supportedCurrencies: walletConnectSupportedCurrencies,
      });

      if ('redirect' in result) {
        setValue((state) => {
          state.screen = result.redirect;
        });
        return null;
      }

      const CheckoutScreen = screen === 'PAY' ? PayScreen : SetupScreen;

      return (
        <CheckoutScreen
          // Make sure we renders the screen when the active wallet changes. This resets all local states.
          key={`${result.activeWallet.id}-${result.activeWallet.chainId}`}
          activeWallet={result.activeWallet}
        />
      );
    }
    case 'TOKEN_SELECT':
      return <TokenSelectScreen />;
    case 'ANY_CRYPTO_NETWORK_SELECT':
      return <NetworkSelectScreen />;
    case 'ANY_CRYPTO_REFUND':
      return <RefundScreen />;
    case 'ANY_CRYPTO_DEPOSIT':
      return <AnyCryptoScreen />;
    case 'ANY_CRYPTO_PROGRESS':
      return <AnyCryptoProgressScreen />;
    case 'INSTALL_WALLET':
      return <InstallWalletScreen />;
    case 'SUCCESS':
      return <SuccessScreen />;
    case 'CANCELED':
      return <CanceledScreen />;
    case 'PROCESSING':
      return <ProcessingScreen />;
    case 'ERROR':
      return <ErroredScreen />;
    default:
      return null;
  }
};

export const ScreenManager = () => {
  // Initialize Mirador ID tracking for fraud detection
  // This hook reads Mirador IDs (MUID, SID) from browser cookies set by Stripe.js
  useMiradorIds();

  const overlay = useSelector((state) => state.overlay);
  return (
    <view.div
      css={{
        // stack: 'y' so children get full height by default
        stack: 'y',
        alignX: 'center',
        paddingTop: 'space.500',
        [MEDIA_QUERIES.mediumWidth]: {
          paddingTop: '0',
        },
      }}
      data-testid="v2-screen-manager"
    >
      <ScreenComponent />
      {overlay === 'NETWORK_SWITCH' ? <NetworkSwitchingOverlay /> : null}
    </view.div>
  );
};
